import { createContext, useContext, useMemo, useSyncExternalStore, type ReactNode } from 'react';
import type { ThemeController, ThemeName, ThemePreference } from './theme';

const ThemeContext = createContext<ThemeController | null>(null);

export function ThemeProvider({
  controller,
  children,
}: {
  controller: ThemeController;
  children: ReactNode;
}) {
  return <ThemeContext.Provider value={controller}>{children}</ThemeContext.Provider>;
}

export interface ThemeHandle {
  theme: ThemeName;
  preference: ThemePreference;
  setPreference: (preference: ThemePreference) => void;
}

/** The current theme and the override setter (Settings uses it later; nothing on the welcome page does). */
export function useTheme(): ThemeHandle {
  const controller = useContext(ThemeContext);
  if (!controller) throw new Error('useTheme() needs a <ThemeProvider> above it.');
  const theme = useSyncExternalStore(controller.subscribe, controller.get, controller.get);
  const preference = useSyncExternalStore(
    controller.subscribe,
    controller.getPreference,
    controller.getPreference,
  );
  return useMemo(
    () => ({ theme, preference, setPreference: controller.setPreference }),
    [theme, preference, controller],
  );
}
